/**
 * Format numbers with K/MM units
 * @param value - Number to format
 * @param includeSymbol - Include $ symbol (default: true)
 * @returns Formatted string like "$5.60 MM" or "5.60 MM"
 */
export const formatNumberByUnits = (value: number, includeSymbol = true): string => {
  if (!value || isNaN(value)) return includeSymbol ? '$0' : '0';
  
  const symbol = includeSymbol ? '$' : '';
  
  if (value >= 1000000) {
    return `${symbol}${(value / 1000000).toFixed(2)} MM`;
  }
  if (value >= 1000) {
    return `${symbol}${(value / 1000).toFixed(2)} K`;
  }
  return `${symbol}${value.toFixed(2)}`;
};

/**
 * Calculate variance percentage between two values
 * @param actual - Actual value (YTD spend)
 * @param baseline - Baseline value to compare against (AOP)
 * @returns Percentage as string (e.g., "-46.31" for under budget or "53.50" for over budget)
 */
export const calculateVariancePercentage = (actual: number, baseline: number): string => {
  if (!baseline || baseline === 0) return '0';
  return (((actual - baseline) / baseline) * 100).toFixed(2);
};

/**
 * Convert millions to absolute values (for chart rendering)
 * Backend returns values in millions (3.62 = 3.62MM)
 * Charts need absolute values (3620000) to properly scale
 * @param valueInMillions - Value in millions from backend
 * @returns Absolute value
 */
export const convertMillionsToAbsolute = (valueInMillions: number): number => {
  return (valueInMillions || 0) * 1000000;
};

/**
 * Adapt monthly totals data for chart rendering
 * Converts values from millions to absolute numbers for proper chart scaling
 * @param monthlyTotals - Array of monthly data from backend (values in millions)
 * @returns Object with months, actualData, forecastData arrays (values as absolute numbers)
 */
export const adaptMonthlyTotalsToChart = (monthlyTotals: any[]) => {
  if (!monthlyTotals || monthlyTotals.length === 0) {
    return { months: [], actualData: [], forecastData: [] };
  }

  const months = monthlyTotals.map(m => `2025-${String(m.month).padStart(2, '0')}`);
  const actualData = monthlyTotals.map(m => convertMillionsToAbsolute(m.totalActualCost));
  const forecastData = monthlyTotals.map(m => convertMillionsToAbsolute(m.totalForecastCost));

  return { months, actualData, forecastData };
};

/**
 * Adapt platform monthly data for chart series
 * Converts platform costs from millions to absolute values
 * @param platformData - Array of platform monthly data from backend
 * @returns Transformed array with absolute values
 */
export const adaptPlatformDataToChart = (platformData: any[]) => {
  if (!platformData || platformData.length === 0) {
    return [];
  }

  return platformData.map(item => ({
    ...item,
    actualCost: convertMillionsToAbsolute(item.actualCost),
    forecastCost: item.forecastCost ? convertMillionsToAbsolute(item.forecastCost) : null
  }));
};

/**
 * Extract unit from formatted number string
 * @param formattedValue - Formatted string like "$5.60 MM"
 * @returns Unit string like "MM" or "K"
 */
export const extractUnit = (formattedValue: string): string => {
  if (formattedValue.includes('MM')) return 'MM';
  if (formattedValue.includes('K')) return 'K';
  return '';
};

